import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';

import { AsyncTaskConfig } from '../async-task-progress/async-task-progress.component';
import { MetaCostAnalysis } from '../../../../../generated.commonjs/cost_analysis_pb';
import { FileMetadataService } from '../../../../shared/file-metadata.service';
import { InputFileMetadata } from '../../../../../generated.commonjs/api_pb';

/**
 * Component for displaying the task progress header with title, filename, and actions.
 */
@Component({
  selector: 'app-task-progress-header',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './task-progress-header.component.html',
  styleUrls: ['./task-progress-header.component.scss'],
})
export class TaskProgressHeaderComponent implements OnInit {
  private dialog = inject(MatDialog);
  private fileMetadataService = inject(FileMetadataService);

  @Input() taskConfig: AsyncTaskConfig | null = null;
  @Input() overallProgress: number = 0;
  @Input() isRunning: boolean = false;
  @Input() isSinglePage: boolean = false;
  @Input() pageNumbers: number[] = [];
  @Input() firstPageNumber: number | null = 1;
  @Input() taskId: string = '';
  @Input() isFailed: boolean = false;
  @Input() isCompleted: boolean = false;
  @Input() isCancelled: boolean = false;
  @Input() costAnalysis: MetaCostAnalysis.AsObject | null = null;
  @Input() fileId: string | null = null; // Issue #167: Direct file ID for display

  @Output() dismissCard = new EventEmitter<void>();
  @Output() openCostDetails = new EventEmitter<MetaCostAnalysis.AsObject>();

  private fileMetadataCache: Map<string, InputFileMetadata> = new Map();

  ngOnInit(): void {
    // Load file metadata if we have a file ID and project ID
    if (this.fileId && this.taskConfig?.projectId) {
      this.loadFileMetadata(this.taskConfig.projectId, this.fileId);
    }
  }

  private async loadFileMetadata(projectId: string, fileId: string): Promise<void> {
    try {
      const response = await this.fileMetadataService.listFileMetadata(projectId).toPromise();
      const files = response?.getFilesList() || [];

      files.forEach(file => {
        this.fileMetadataCache.set(file.getFileId(), file);
      });

      // Validate that the requested file was loaded
      if (fileId && !this.fileMetadataCache.has(fileId)) {
        ClientLoggingService.log(
          'WARN',
          `[TaskProgressHeaderComponent] File ${fileId} not found in project ${projectId}`
        );
      }
    } catch (error) {
      ClientLoggingService.log('WARN', '[TaskProgressHeaderComponent] Error loading file metadata:', error);
    }
  }

  /**
   * Gets the number of pages being processed.
   */
  getPageNumbersLength(): number {
    return this.pageNumbers.length;
  }

  /**
   * Gets page numbers as a formatted string.
   */
  getPageNumbersString(): string {
    return this.pageNumbers.join(', ');
  }

  /**
   * Checks if this is a multi-page task with specific page numbers.
   */
  isMultiPageTaskWithNumbers(): boolean {
    return this.pageNumbers.length > 1;
  }

  /**
   * Emits dismiss event.
   */
  /**
   * Gets the task type display name based on the task configuration
   */
  getTaskTypeDisplayName(): string {
    // Use the actual taskType from the task data if available
    if (this.taskConfig?.taskType) {
      switch (this.taskConfig.taskType) {
        case 'compliance-report':
          return 'Compliance Report';
        case 'code-applicability':
          return 'Code Analysis';
        case 'plan_ingestion':
        default:
          return 'Ingestion';
      }
    }

    // Fallback: Check filename for task type hints (for backward compatibility)
    if (this.taskConfig?.filename?.toLowerCase().includes('compliance report')) {
      return 'Compliance Report';
    }

    if (
      this.taskConfig?.filename?.toLowerCase().includes('code applicability') ||
      this.taskConfig?.filename?.toLowerCase().includes('analysis')
    ) {
      return 'Code Analysis';
    }

    // Default to ingestion for plan ingestion tasks
    return 'Ingestion';
  }

  /**
   * Gets the appropriate icon name based on task status
   */
  getIconName(): string {
    if (this.isFailed) {
      return 'error';
    }
    if (this.isCancelled) {
      return 'cancel';
    }
    if (this.isCompleted) {
      return 'check_circle';
    }
    if (this.isRunning) {
      return 'file_download';
    }
    return 'file_download'; // Default for pending
  }

  /**
   * Gets the appropriate icon color based on task status
   */
  getIconColor(): string {
    if (this.isFailed) {
      return 'warn'; // Red color
    }
    if (this.isCancelled) {
      return 'disabled'; // Grey color
    }
    if (this.isCompleted) {
      return 'primary'; // Green color
    }
    return 'default'; // Default color for running/pending
  }

  /**
   * Gets the display filename for the task
   * Issue #167: Generic logic for all file-scoped tasks
   */
  getDisplayFilename(): string {
    // For file-scoped tasks (ingestion, code applicability, compliance reports)
    if (this.isFileScopedTask()) {
      if (this.fileId) {
        // Try to get actual filename from file metadata cache
        const fileMetadata = this.fileMetadataCache.get(this.fileId);
        if (fileMetadata) {
          return fileMetadata.getFileName();
        }

        // Fallback if metadata not loaded yet
        return `File ${this.fileId} (Page ${this.firstPageNumber})`;
      }

      // No file ID - legacy project or using legacy filename
      return this.taskConfig?.filename || `Legacy Project (Page ${this.firstPageNumber})`;
    }

    // For non-file-scoped tasks, show the actual filename
    return this.taskConfig?.filename || 'Unknown File';
  }

  /**
   * Determines if this task is scoped to a specific file
   * Issue #167: Generic check for all file-scoped task types
   */
  private isFileScopedTask(): boolean {
    const taskType = this.taskConfig?.taskType;

    // Check task type
    if (taskType === 'code-applicability' || taskType === 'compliance-report' || taskType === 'plan_ingestion') {
      return true;
    }

    return false;
  }

  /**
   * Gets the appropriate file icon based on file metadata
   * Issue #167: Generic logic using file metadata for all file-scoped tasks
   */
  getFileIcon(): string {
    // For file-scoped tasks, use file metadata to determine icon
    if (this.isFileScopedTask() && this.fileId) {
      const fileMetadata = this.fileMetadataCache.get(this.fileId);
      if (fileMetadata) {
        const mimeType = fileMetadata.getMimeType();

        // Determine icon based on MIME type
        if (mimeType === 'application/pdf') {
          return 'picture_as_pdf';
        } else if (mimeType.startsWith('image/')) {
          return 'image';
        } else if (mimeType === 'application/dwg' || mimeType === 'application/dxf') {
          return 'architecture';
        }
      }
    }

    // Default generic file icon
    return 'description';
  }

  onDismissCard(): void {
    this.dismissCard.emit();
  }

  /**
   * Checks if task has cost analysis data
   */
  hasCostAnalysis(): boolean {
    return !!(this.costAnalysis && (this.costAnalysis.totalCostUsd > 0 || this.costAnalysis.totalTokens > 0));
  }

  /**
   * Issue #612: Gets the cost data source from the cost analysis.
   * Returns the enum value indicating why cost data is in its current state.
   * Supports both camelCase (TS) and snake_case (Firestore) field names.
   */
  getCostDataSource(): string | null {
    const costAnalysisAny = this.costAnalysis as any;
    return costAnalysisAny?.costDataSource || costAnalysisAny?.cost_data_source || null;
  }

  /**
   * Issue #612: Gets a user-friendly message for the cost data source.
   * Returns null if normal LLM cost data is available.
   */
  getCostDataSourceMessage(): { message: string; tooltip: string } | null {
    const source = this.getCostDataSource();

    // Map enum values to user-friendly messages
    // Proto enum values are serialized as integers or string names
    const sourceMessages: Record<string, { message: string; tooltip: string }> = {
      // String enum names (from proto JSON serialization)
      COST_DATA_SOURCE_CACHED_REPORT: {
        message: 'Reused cached report',
        tooltip: 'Task used existing cached data, no new LLM calls were made',
      },
      COST_DATA_SOURCE_CANCELLED_EARLY: {
        message: 'Cancelled before processing',
        tooltip: 'Task was cancelled before any LLM calls were made',
      },
      COST_DATA_SOURCE_FAILED_EARLY: {
        message: 'Failed before processing',
        tooltip: 'Task failed before any LLM calls could be made',
      },
      COST_DATA_SOURCE_NON_LLM: {
        message: 'No LLM required',
        tooltip: 'Task completed using traditional processing, no LLM calls needed',
      },
      COST_DATA_SOURCE_MOCK: {
        message: 'Test mode',
        tooltip: 'Task ran in test/mock mode with simulated LLM calls',
      },
      // Integer enum values (from proto binary serialization)
      '2': {
        // CACHED_REPORT
        message: 'Reused cached report',
        tooltip: 'Task used existing cached data, no new LLM calls were made',
      },
      '3': {
        // CANCELLED_EARLY
        message: 'Cancelled before processing',
        tooltip: 'Task was cancelled before any LLM calls were made',
      },
      '4': {
        // FAILED_EARLY
        message: 'Failed before processing',
        tooltip: 'Task failed before any LLM calls could be made',
      },
      '5': {
        // NON_LLM
        message: 'No LLM required',
        tooltip: 'Task completed using traditional processing, no LLM calls needed',
      },
      '6': {
        // MOCK
        message: 'Test mode',
        tooltip: 'Task ran in test/mock mode with simulated LLM calls',
      },
    };

    // Return null for LLM_GENERATED (1), UNSPECIFIED (0), or normal cost data
    if (
      !source ||
      source === 'COST_DATA_SOURCE_UNSPECIFIED' ||
      source === 'COST_DATA_SOURCE_LLM_GENERATED' ||
      source === '0' ||
      source === '1'
    ) {
      return null;
    }

    return sourceMessages[source] || null;
  }

  /**
   * Issue #612: Checks if task has a special cost data source that should be displayed.
   * Returns true if the cost data source is something other than normal LLM generation.
   */
  hasSpecialCostDataSource(): boolean {
    return this.getCostDataSourceMessage() !== null;
  }

  /**
   * Determines if we should show a cost analysis placeholder
   * Shows placeholder for compliance report and code analysis tasks that completed but have no cost data
   */
  shouldShowCostPlaceholder(): boolean {
    if (!this.taskConfig) return false;

    // Only show placeholder for tasks that typically generate cost data
    const taskType = this.taskConfig.taskType;
    const shouldHaveCostData = taskType === 'compliance-report' || taskType === 'code-applicability';

    // Only show if task is completed (these tasks should have cost data when complete)
    const isCompleted = this.isCompleted;

    return shouldHaveCostData && isCompleted;
  }

  /**
   * Opens the cost details dialog
   */
  onOpenCostDetails(): void {
    if (this.costAnalysis) {
      this.openCostDetails.emit(this.costAnalysis);
    }
  }

  /**
   * Formats cost as currency with appropriate precision
   */
  formatCost(cost: number): string {
    if (cost < 0.01) {
      return `$${cost.toFixed(6)}`;
    } else if (cost < 1) {
      return `$${cost.toFixed(4)}`;
    } else {
      return `$${cost.toFixed(2)}`;
    }
  }

  /**
   * Formats token count with thousands separators
   */
  formatTokens(tokens: number): string {
    return tokens.toLocaleString();
  }
}
